import { Directive, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExecutionPlanService } from '../services/execution-plan/execution-plan.service';
import { ProjectService } from '../services/project/project.service';
import { WorkflowService } from '../services/workflow/workflow.service';
import { AppStateService } from '../services/app-state/app-state.service';
import { SecretsService } from '../services/secrets/secrets.service';
import { ConnectionService } from '../services/connection/connection.service';
import { ProjectUser, ProjectVersion, Workflow } from '@app/shared/interfaces';
import { ConnectionType } from '../interfaces/enums.interface';
import { isBoxRegistryConfig } from '../interfaces/connection.interface';
import { Secret } from '../interfaces/secrets.interface';
import { CRON_SCHEDULE_OPTIONS, CronScheduleOption } from '../constants/cron-schedule-options';
import { CronValidationUtils } from '../utils/cron-validation.utils';
import { DisplayUtils } from '../utils/display.utils';
import { FormValidationUtils } from '../utils/form-validation.utils';
import { ErrorHandlingUtils } from '../utils/error-handling.utils';
import { parseServerDate } from '../helpers/date-formatting.helper';

export interface BaseRoutineDialogData {
  projectId: number;
}

@Directive()
export abstract class BaseRoutineDialogComponent implements OnInit {
  routineForm!: FormGroup;
  loading = signal(false);
  error = signal<string | null>(null);
  projectUsers = signal<ProjectUser[]>([]);
  projectWorkflows: Workflow[] = [];
  projectVersions: ProjectVersion[] = [];
  allVersions: ProjectVersion[] = [];
  headVersionId: number | null = null;
  protected currentLoadingVersionId: number | null = null;
  showCustomCronInput = false;
  hasOnlyOneVersion = false;

  // Cron schedule options (shared constant)
  cronScheduleOptions: CronScheduleOption[] = CRON_SCHEDULE_OPTIONS;
  
  // Webhook type options
  webhookTypeOptions = [
    { value: 'Box', label: 'Box' },
    { value: 'DropBox', label: 'DropBox' }
  ];
  
  // Source options
  sourceOptions = [
    { value: 'manual', label: 'Manual Only' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'webhook', label: 'Webhook' }
  ];

  // Secrets for webhook secret selectors
  availableSecrets = signal<Secret[]>([]);

  // Whether the project has a registry-backed Box connection (SCRUM-636):
  // its Box app holds the webhook signature keys, so manual webhook secrets
  // are only a legacy fallback for Box webhook routines.
  hasRegistryBoxConnection = signal(false);

  // Dependency injection
  protected fb = inject(FormBuilder);
  protected executionPlanService = inject(ExecutionPlanService);
  protected projectService = inject(ProjectService);
  protected notebookService = inject(WorkflowService);
  protected appStateService = inject(AppStateService);
  protected secretsService = inject(SecretsService);
  protected connectionService = inject(ConnectionService);
  protected destroyRef = inject(DestroyRef);

  // Abstract properties that must be implemented by child classes
  abstract get data(): BaseRoutineDialogData;

  ngOnInit(): void {
    this.routineForm = this.createRoutineForm();
    this.loadProjectData();
    this.onInitHook();

    // Defensive guard: clear useHead whenever crontab becomes non-empty.
    // This catches the edge case where the user selects the 'custom' schedule
    // option (crontab starts as ''), then re-enables useHead while crontab is
    // still empty, then types a cron expression — which would produce an
    // invalid scheduled+HEAD combination that the backend rejects.
    this.routineForm.get('crontab')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        if (value?.trim() && this.routineForm.get('useHead')?.value) {
          this.routineForm.patchValue({ useHead: false });
        }
      });

    // The webhook-secret requirement depends on the selected webhook type
    // (Box routines backed by a registry connection don't need manual secrets).
    this.routineForm.get('webhookType')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateWebhookSecretValidators());
  }

  // Hook for child classes to add additional initialization
  protected onInitHook(): void {}

  protected createRoutineForm(): FormGroup {
    const baseForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      runsAsId: [null, [Validators.required]],
      workflowId: [null],
      crontab: [''],
      cronScheduleType: [''], // For schedule type selection
      // Webhook fields
      source: ['manual'], // Default to Manual
      webhookType: [null],
      webhookSecretId1: [null],
      webhookSecretId2: [null],
      arguments: ['', [FormValidationUtils.jsonObjectValidator]],
      // HEAD version execution (manual routines only)
      useHead: [false]
    });

    // Allow child classes to add additional form controls
    this.addCustomFormControls(baseForm);
    return baseForm;
  }

  // Hook for child classes to add custom form controls
  protected addCustomFormControls(_form: FormGroup): void {}

  protected loadProjectData(): void {
    this.loading.set(true);
    this.error.set(null);

    // Reset form state
    this.projectVersions = [];
    this.allVersions = [];
    this.projectUsers.set([]);
    this.projectWorkflows = [];

    // Load project versions and users
    this.loadProjectVersions();

    // Load secrets for webhook configuration
    this.loadSecrets();

    // Load connections to detect registry-backed Box connections (SCRUM-636)
    this.loadBoxConnections();
  }

  protected loadSecrets(): void {
    this.secretsService.getSecrets(this.data.projectId).subscribe({
      next: (secrets) => this.availableSecrets.set(secrets),
      error: () => this.availableSecrets.set([])
    });
  }

  protected loadBoxConnections(): void {
    this.connectionService.getConnections(this.data.projectId).subscribe({
      next: (connections) => {
        this.hasRegistryBoxConnection.set(
          connections.some(c => c.type === ConnectionType.Box && isBoxRegistryConfig(c.config))
        );
        this.updateWebhookSecretValidators();
      },
      error: () => this.hasRegistryBoxConnection.set(false)
    });
  }

  protected loadProjectVersions(): void {
    let usersLoaded = false;
    let versionsLoaded = false;

    const checkLoadingComplete = () => {
      if (usersLoaded && versionsLoaded) {
        this.loading.set(false);
        this.onProjectDataLoaded();
      }
    };

    // Load project versions
    this.projectService.getProject(this.data.projectId).subscribe({
      next: (project: any) => {
        this.allVersions = project?.versions || [];
        const head = this.allVersions.find(
          (v) => v.majorVersion === 0 && v.minorVersion === 0,
        );
        const appHeadId = this.appStateService.headVersionId?.();
        this.headVersionId =
          head?.versionId ??
          project?.currentVersionId ??
          (appHeadId && appHeadId > 0 ? appHeadId : null);
        this.processProjectVersions(this.allVersions);
        versionsLoaded = true;
        checkLoadingComplete();
      },
      error: (error: any) => {
        this.handleLoadingError('versions', error);
      }
    });

    // Load project users
    this.projectService.getProjectUsers(this.data.projectId).subscribe({
      next: (users: any) => {
        this.projectUsers.set(users);
        this.setDefaultUser(users);
        usersLoaded = true;
        checkLoadingComplete();
      },
      error: (error: any) => {
        this.handleLoadingError('users', error);
      }
    });
  }

  protected processProjectVersions(versions: any[]): void {
    // Filter out head versions (majorVersion=0, minorVersion=0)
    this.projectVersions = versions.filter(version =>
      !(version.majorVersion === 0 && version.minorVersion === 0)
    );

    // Set flag for single version
    this.hasOnlyOneVersion = this.projectVersions.length === 1;

    // Allow child classes to handle version processing
    this.onVersionsProcessed();
  }

  getHeadVersionId(): number | null {
    if (this.headVersionId != null && this.headVersionId > 0) {
      return this.headVersionId;
    }
    const head = this.allVersions.find(
      (v) => v.majorVersion === 0 && v.minorVersion === 0,
    );
    if (head) {
      this.headVersionId = head.versionId;
      return this.headVersionId;
    }
    const appHeadId = this.appStateService.headVersionId?.();
    if (appHeadId && appHeadId > 0) {
      return appHeadId;
    }
    // Do NOT fall back to appStateService.versionId() here: that is the version
    // currently open, not HEAD. Returning null keeps the dropdown empty rather
    // than silently loading the wrong version's workflows while useHead is set.
    return null;
  }

  getLatestVersionId(): number | null {
    if (this.projectVersions.length === 0) return null;

    if (this.projectVersions.length === 1) {
      return this.projectVersions[0].versionId;
    }

    const latestVersion = [...this.projectVersions].sort(
      (a, b) =>
        (parseServerDate(b.createdAt)?.getTime() ?? 0) -
        (parseServerDate(a.createdAt)?.getTime() ?? 0),
    )[0];
    return latestVersion.versionId;
  }

  getTargetVersionId(): number | null {
    const isHead = !!this.routineForm?.get('useHead')?.value;
    if (isHead) {
      return this.getHeadVersionId();
    }
    const formVersionId = this.routineForm?.get('versionId')?.value;
    if (formVersionId != null) {
      return Number(formVersionId);
    }
    return this.getLatestVersionId() ?? this.getHeadVersionId();
  }

  loadProjectWorkflows(targetVersionId?: number | null): void {
    const projectId = this.data.projectId;
    const versionId = targetVersionId ?? this.getTargetVersionId();

    if (projectId == null || versionId == null) {
      return;
    }

    this.currentLoadingVersionId = versionId;

    this.notebookService
      .getAllWorkflows(projectId, versionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (workflows: any) => {
          if (this.currentLoadingVersionId !== versionId) {
            return;
          }
          if (workflows && Array.isArray(workflows)) {
            const currentWorkflowId =
              this.routineForm?.get('workflowId')?.value;
            const currentWorkflow = this.projectWorkflows.find(
              (w) => w.workflowId === currentWorkflowId,
            );
            const currentWorkflowName = currentWorkflow?.name;

            this.updateWorkflows(workflows);
            this.reconcileSelectedWorkflow(
              currentWorkflowId,
              currentWorkflowName,
            );
          }
        },
        error: (error: any) => {
          if (this.currentLoadingVersionId !== versionId) {
            return;
          }
          console.error(
            `Failed to load workflows for version ${versionId}:`,
            error,
          );
          // A failed load must not leave the previous version's workflows (and
          // their selection) in the dropdown, otherwise the user could create a
          // routine against a workflow absent from the selected version.
          this.updateWorkflows([]);
          this.reconcileSelectedWorkflow(null, null);
        },
      });
  }

  protected reconcileSelectedWorkflow(
    previousWorkflowId: number | null | undefined,
    previousWorkflowName?: string | null,
  ): void {
    const workflowControl = this.routineForm?.get('workflowId');
    if (!workflowControl) return;

    const currentId = workflowControl.value;

    if (currentId == null && previousWorkflowId == null) {
      return;
    }

    const idToCheck = currentId ?? previousWorkflowId;

    const existingById = this.projectWorkflows.find(
      (w) => w.workflowId === idToCheck,
    );
    if (existingById) {
      if (currentId !== existingById.workflowId) {
        workflowControl.setValue(existingById.workflowId, { emitEvent: false });
      }
      return;
    }

    const nameToMatch =
      previousWorkflowName ||
      (idToCheck != null ? this.getWorkflowNameById(idToCheck) : null);
    if (nameToMatch) {
      const existingByName = this.projectWorkflows.find(
        (w) => w.name.toLowerCase() === nameToMatch.toLowerCase(),
      );
      if (existingByName) {
        workflowControl.setValue(existingByName.workflowId, {
          emitEvent: false,
        });
        return;
      }
    }

    workflowControl.setValue(null, { emitEvent: false });
    workflowControl.updateValueAndValidity();
  }

  protected updateWorkflows(workflows: Workflow[]): void {
    this.projectWorkflows = workflows
      .filter(workflow => workflow.workflowId && workflow.name) // Filter out invalid workflows
      .map(workflow => ({
        workflowId: workflow.workflowId,
        projectId: this.data.projectId,
        versionId:
          workflow.versionId ||
          this.currentLoadingVersionId ||
          this.appStateService.versionId(),
        name: workflow.name,
        defaultState: workflow.defaultState || '',
        headlessOnly: workflow.headlessOnly ?? false,
        description: workflow.description || '',
        createdAt: workflow.createdAt || new Date().toISOString(),
        isActive: workflow.isActive ?? true
      }));
  }

  protected setDefaultUser(users: ProjectUser[]): void {
    if (users.length === 0) return;

    // Only set default user if no user is already selected
    const currentRunsAsId = this.routineForm.get('runsAsId')?.value;
    if (currentRunsAsId) {
      return; // User already selected, don't override
    }

    const currentUser = this.appStateService.user();
    let targetUserId: number;

    if (currentUser) {
      const userInProject = users.find(u => u.userId === currentUser.userId);
      targetUserId = userInProject?.userId ?? users[0].userId;
    } else {
      targetUserId = users[0].userId;
    }

    this.routineForm.patchValue({ runsAsId: targetUserId });
  }

  protected handleLoadingError(entityName: string, error: any): void {
    this.error.set(`Failed to load project ${entityName}`);
    this.loading.set(false);
    console.error(`Error loading ${entityName}:`, error);
  }

  // Hooks for child classes
  protected onProjectDataLoaded(): void {}
  protected onVersionsProcessed(): void {}

  // Cron schedule handling
  onCronScheduleChange(event: any): void {
    const selectedValue = event.value;

    if (selectedValue === 'custom') {
      this.showCustomCronInput = true;
      this.routineForm.patchValue({ crontab: '' });
    } else {
      this.showCustomCronInput = false;
      this.routineForm.patchValue({ crontab: selectedValue });
    }

    // HEAD execution is only valid for manual (unscheduled) routines.
    // Auto-clear useHead when a schedule is selected so the user cannot
    // accidentally save a scheduled routine with use_head=true (the backend
    // would reject that combination).
    if (selectedValue && selectedValue !== '') {
      this.routineForm.patchValue({ useHead: false });
    }
  }

  // Expose utility classes for template use
  readonly CronValidationUtils = CronValidationUtils;
  readonly DisplayUtils = DisplayUtils;
  readonly FormValidationUtils = FormValidationUtils;
  readonly ErrorHandlingUtils = ErrorHandlingUtils;

  // Template helper methods (simple wrappers)
  getUserNameById = (userId: number | null | undefined): string =>
    this.DisplayUtils.getUserNameById(userId, this.projectUsers());

  getWorkflowNameById = (workflowId: number | null | undefined): string => 
    this.DisplayUtils.getWorkflowNameById(workflowId, this.projectWorkflows);

  getVersionNameById = (versionId: number | null | undefined): string => 
    this.DisplayUtils.getVersionNameById(versionId, this.projectVersions);

  getVersionDisplay = (version: ProjectVersion): string => 
    this.DisplayUtils.getVersionDisplay(version);

  formatVersionDate = (timestamp: string): string => 
    this.DisplayUtils.formatVersionDate(timestamp);

  isHeadVersion = (version: ProjectVersion): boolean => 
    this.DisplayUtils.isHeadVersion(version);

  validateCustomCron = (crontab: string): boolean => 
    this.CronValidationUtils.validateCustomCron(crontab, this.executionPlanService);

  getCronValidationError = (crontab: string): string | null => 
    this.CronValidationUtils.getCronValidationError(crontab, this.executionPlanService);

  getSelectedCronScheduleLabel(): string {
    const crontab = this.routineForm.get('crontab')?.value;
    if (!crontab) return 'Manual only (no schedule)';

    const option = this.cronScheduleOptions.find(opt => opt.value === crontab);
    return option ? option.label : 'Custom schedule';
  }

  getFormFieldError(fieldName: string): string | null {
    const fieldLabels = this.getFieldLabels();
    return FormValidationUtils.getFormFieldError(this.routineForm, fieldName, fieldLabels);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Arguments (JSON) editor helpers — the field is rendered with
  // app-code-editor, which is not a matInput, so the dialogs bind these
  // instead of formControlName.

  /** Current value of the arguments control, for [content] binding. */
  get argumentsContent(): string {
    return this.routineForm?.get('arguments')?.value || '';
  }

  /** Push editor changes into the arguments form control. */
  onArgumentsContentChange(value: string): void {
    const control = this.routineForm.get('arguments');
    if (!control) return;
    control.setValue(value ?? '');
    control.markAsDirty();
  }

  /** Mark the arguments control touched when the user starts editing. */
  onArgumentsStartEdit(): void {
    this.routineForm.get('arguments')?.markAsTouched();
  }

  /**
   * Pretty-print a stored arguments JSON string for editing.
   * Empty/`"{}"` displays as an empty editor.
   */
  protected formatArgumentsForDisplay(stored: string | null | undefined): string {
    const raw = (stored ?? '').trim();
    if (!raw || raw === '{}') {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  // Allow child classes to customize field labels
  protected getFieldLabels(): { [key: string]: string } {
    return {
      name: 'Routine Name',
      description: 'Description',
      runsAsId: 'Runs as User',
      workflowId: 'Workflow',
      versionId: 'Version',
      crontab: 'Schedule',
      source: 'Source Type',
      webhookType: 'Webhook Type',
      // deepcode ignore javascript/HardcodedNonCryptoSecret: UI field label in a display-name map, not a secret
      webhookSecretId1: 'Webhook Secret 1',
      // deepcode ignore javascript/HardcodedNonCryptoSecret: UI field label in a display-name map, not a secret
      webhookSecretId2: 'Webhook Secret 2',
      arguments: 'Arguments (JSON)'
    };
  }

  // Form validation helper
  protected validateForm(): boolean {
    if (this.routineForm.invalid) {
      FormValidationUtils.markAllFieldsAsTouched(this.routineForm);
      return false;
    }

    // Validate custom cron expression if provided
    const crontab = (this.routineForm.get('crontab')?.value || '').trim();
    if (crontab && !this.CronValidationUtils.validateCustomCron(crontab, this.executionPlanService)) {
      this.error.set(this.CronValidationUtils.getCronValidationError(crontab, this.executionPlanService) ||
        'Invalid cron expression format. Please use the format: minute hour day month weekday (e.g., "0 2 * * *")');
      return false;
    }

    return true;
  }

  // Server-side validation helper
  protected validateWithServer(request: any): Observable<boolean> {
    return this.executionPlanService.validatePlan(this.data.projectId, request).pipe(
      map((response: any) => {
        if (!response.valid) {
          this.error.set(response.errors.join(', '));
          return false;
        }
        if (response.warnings.length > 0) {
          console.warn('Routine validation warnings:', response.warnings);
        }
        return true;
      })
    );
  }

  // Webhook form helpers
  onSourceChange(event?: any): void {
    const source = event?.value ?? this.routineForm.get('source')?.value;

    if (source === 'webhook') {
      this.routineForm.get('webhookType')?.setValidators([Validators.required]);
      // Webhook routines cannot have a cron schedule
      this.routineForm.patchValue({ crontab: '', cronScheduleType: '' });
      this.showCustomCronInput = false;
    } else {
      this.routineForm.get('webhookType')?.clearValidators();
      // Clear webhook fields when switching away
      this.routineForm.patchValue({ webhookType: null, webhookSecretId1: null, webhookSecretId2: null });
    }

    this.routineForm.get('webhookType')?.updateValueAndValidity();
    this.updateWebhookSecretValidators();
  }

  /**
   * Manual webhook secrets are only required when the signature keys can't be
   * resolved automatically: for Box routines, a registry-backed Box connection
   * provides the keys via its Box app (SCRUM-636), so secrets become an
   * optional legacy fallback.
   */
  protected updateWebhookSecretValidators(): void {
    const secret1 = this.routineForm?.get('webhookSecretId1');
    if (!secret1) return;

    if (this.isWebhookSource() && !this.boxSecretsOptional()) {
      secret1.setValidators([Validators.required]);
    } else {
      secret1.clearValidators();
    }
    secret1.updateValueAndValidity();
    this.routineForm.get('webhookSecretId2')?.updateValueAndValidity();
  }

  /** True when the Box app registry supplies the webhook signature keys. */
  boxSecretsOptional(): boolean {
    return (
      this.routineForm?.get('webhookType')?.value === 'Box' &&
      this.hasRegistryBoxConnection()
    );
  }

  /** Pre-registry routines may still carry manual webhook secrets. */
  hasLegacyWebhookSecrets(): boolean {
    return (
      this.routineForm?.get('webhookSecretId1')?.value != null ||
      this.routineForm?.get('webhookSecretId2')?.value != null
    );
  }

  isWebhookSource(): boolean {
    return this.routineForm.get('source')?.value === 'webhook';
  }

  isScheduledSource(): boolean {
    return this.routineForm.get('source')?.value === 'scheduled';
  }

  getSecretNameById(secretId: number | null | undefined): string {
    if (!secretId) return '';
    const secret = this.availableSecrets().find(s => s.secretId === secretId);
    return secret?.name ?? `Secret #${secretId}`;
  }

  // Abstract methods that child classes must implement
  abstract onCancel(): void;
  abstract onSave(): void;
}
